import { useMemo, useState } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import type { MasterControlAuditLog } from "@/hooks/useMasterControl";
import { MCAuditLogDrawer } from "./MCAuditLogDrawer";

type MCAuditLogTableProps = {
  logs: MasterControlAuditLog[];
};

function formatTimestamp(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
}

export function MCAuditLogTable({ logs }: MCAuditLogTableProps) {
  const [selected, setSelected] = useState<MasterControlAuditLog | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const sortedLogs = useMemo(
    () =>
      [...logs].sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
      ),
    [logs],
  );

  const statusBadge = (entry: MasterControlAuditLog) => (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
        entry.success ? "bg-emerald-500/20 text-emerald-200" : "bg-rose-500/20 text-rose-200"
      }`}
    >
      {entry.success ? "Success" : "Failed"}
    </span>
  );

  const handleOpen = (entry: MasterControlAuditLog) => {
    setSelected(entry);
    setDrawerOpen(true);
  };

  return (
    <>
      <Card className="rounded-2xl border border-border bg-background/60 shadow-none">
        <CardHeader className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <CardTitle className="text-lg font-semibold text-foreground">Owner Audit Log</CardTitle>
            <p className="text-sm text-muted-foreground">
              Every destructive action recorded with actor, target, and status.
            </p>
          </div>
          <p className="text-xs text-muted-foreground">{sortedLogs.length} entries</p>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Timestamp</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedLogs.map((entry) => (
                <TableRow key={entry.id} className="cursor-pointer" onClick={() => handleOpen(entry)}>
                  <TableCell className="text-sm text-foreground/80">{formatTimestamp(entry.timestamp)}</TableCell>
                  <TableCell className="text-sm text-foreground/80">{entry.actor_user_id ?? "owner"}</TableCell>
                  <TableCell className="text-sm font-semibold text-foreground">{entry.action}</TableCell>
                  <TableCell className="text-sm text-foreground/80">
                    {entry.target_identifier ?? entry.target_type ?? "—"}
                  </TableCell>
                  <TableCell>{statusBadge(entry)}</TableCell>
                </TableRow>
              ))}
              {!sortedLogs.length && (
                <TableRow>
                  <TableCell colSpan={5} className="py-6 text-center text-sm text-muted-foreground">
                    No owner actions recorded yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <MCAuditLogDrawer log={selected} open={drawerOpen} onOpenChange={setDrawerOpen} />
    </>
  );
}

export default MCAuditLogTable;
